import type { MouseEvent } from 'react';
import React from 'react';

import { useLocalization } from '../../providers/LocaleProvider';
import type { Color } from '../../theme/colors/types';
import { Flex } from '../Flex';
import SwatchPill from './components/SwatchPill';
import {
  SWATCH_GAP_SIZE_V2,
  SWATCH_IMAGE_BORDER_COLOR_V2,
  SWATCH_IMAGE_SIZE_V2,
  SWATCH_PILL_CONTAINER_HEIGHT_V2,
  VISIBLE_SWATCHES_COUNT_V2,
} from './constants/constants';
import { useSwatchData } from './hooks/use-swatch-data';
import { useVariationDrawer } from './hooks/use-variation-drawer';
import type { ProductListVariation, TrackingContext } from './types/types';

const SWATCH_IMAGE_FLEX_SHRINK = 0;

interface VariationButtonV2Props {
  sku: string;
  variations: ProductListVariation[];
  url: string;
  trackingContext: TrackingContext;
}

const swatchContainerStyles = {
  borderColor: SWATCH_IMAGE_BORDER_COLOR_V2,
  height: SWATCH_PILL_CONTAINER_HEIGHT_V2,
  p: 'sp_2',
  display: 'flex' as const,
  justifyContent: 'flex-start' as const,
  maxWidth: '92px',
  width: '92px',
};
const arrowIconStyles = {
  fill: 'sys.neutral.icon.default' as Color,
  name: 'arrowSmall' as const,
  size: 11,
  transform: 'scaleX(-1)',
};

const VariationButton = ({ sku, variations, url, trackingContext }: VariationButtonV2Props) => {
  const { r } = useLocalization();

  const { swatches, allSwatchesCount, isMobile } = useSwatchData(variations);

  const { handleClick, clickTrackingHandler } = useVariationDrawer({
    sku,
    url,
    variations,
    trackingContext,
  });

  if (swatches.length === 0) return null;

  return (
    <Flex
      flexDirection="column"
      alignSelf="flex-start"
      onClick={(event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        handleClick();
        clickTrackingHandler(event);
      }}
      data-testid="variation-button"
    >
      <SwatchPill
        swatches={swatches}
        allSwatchesCount={allSwatchesCount}
        visibleSwatchesCount={VISIBLE_SWATCHES_COUNT_V2}
        isMobile={isMobile}
        ariaLabel={r.t('h24_product_count_color_material', { count: swatches.length })}
        swatchContainerStyles={swatchContainerStyles}
        imageOptionStylesOverride={{ borderColor: SWATCH_IMAGE_BORDER_COLOR_V2 }}
        swatchImageSize={SWATCH_IMAGE_SIZE_V2}
        swatchGapSize={SWATCH_GAP_SIZE_V2}
        arrowIconStyles={arrowIconStyles}
        swatchImageFlexShrink={SWATCH_IMAGE_FLEX_SHRINK}
        withCompactMoreSwatches
        withFixedMobileWidth={false}
      />
    </Flex>
  );
};

export default VariationButton;
